import React from 'react';
import styled from 'styled-components';
import useTranslation from '@src/hooks/useTranslation';
import { useSelector } from 'react-redux';

import useCurrentCourse from '@src/hooks/useCurrentCourse';
import { formatCourseTimeMinutes, showStudent, accessRightText } from '@src/utils/course';
import { getIsUnlimitedDaysVisible, coursePriceVisible } from '@src/utils/hardcode';
import * as selectors from '@src/store/selectors';

const CourseInfoWrapper = styled.div`
	width: 100%;
	display: grid;
	padding: 16px 0px;
	/* border-top: 1px solid #F0F0F0; */
	border-bottom: 1px solid #F0F0F0;
	grid-template-columns: repeat(2, 1fr);
	grid-column-gap: 4px;
	border-bottom-width: ${props => (props.isCoursePriceVisible ? '1px' : '0px')};

	@media
		${props => props.theme.device.tablet},
		${props => props.theme.device.smTablet},
		${props => props.theme.device.mobile}
	{
		border-bottom: none;
	}

	@media ${props => props.theme.device.tablet} {
		padding-bottom: 0;
	}

	@media ${props => props.theme.device.mobile} {
		grid-template-columns: repeat(1, 1fr);
	}
`;

const ListItemLeftWrapper = styled.div`
	width: 100%;
	margin: 0;
	@media ${props => props.theme.device.lgDesktop} {
		width: 168px
	}

	@media ${props => props.theme.device.desktop} {
		width: 100px
	}

	@media
		${props => props.theme.device.tablet},
		${props => props.theme.device.mobile}
	{
		margin-bottom: 4px;
	}
`;

const ListItemRightWrapper = styled.div`
	width: 100%;
	margin: 0;

	@media ${props => props.theme.device.lgDesktop} {
		width: 216px;
	}

	@media ${props => props.theme.device.desktop} {
		width: 212px
	}
`;

const InfoItem = styled.div`
	position: relative;
	margin-left: 16px;

	&:before {
		content: '';
		position: absolute;
		top: 50%;
		left: -12px;
		transform: translateY(-50%);
		width: 4px;
		height: 4px;
		background: #676767;
		border-radius: 100%;
	}

	&:not(:last-child) {
		margin-bottom: 4px;
	}
`;

const InfoTitle = styled.span`
	margin-right: 4px;
	font-size: 12px;

	@media ${props => props.theme.device.desktop} {
		display: none;
	}

	@media ${props => props.theme.device.mobile} {
		font-size: 14px;
	}
`;

const InfoContent = styled.span`
	font-size: 14px;
	font-weight: 700;
	color: #333333;

	@media ${props => props.theme.device.mobile} {
		font-size: 16px;
	}
`;

const CourseInfo = (): JSX.Element => {
	const { t } = useTranslation();
	const {
		currentCourse: {
			video_duration_seconds,
			class_level,
			students,
			advance_settings: advanceSettings,
		},
	} = useCurrentCourse();
	const domain = useSelector(selectors.getDomain) as string;
	const isUnlimitedDaysVisible = getIsUnlimitedDaysVisible(domain);
	const isCoursePriceVisible = coursePriceVisible(domain);
	const showStudents = showStudent({ advanceSettings, students });
	const accessRight = accessRightText({ advanceSettings, t });

	return (
		<CourseInfoWrapper isCoursePriceVisible={isCoursePriceVisible}>
			<ListItemLeftWrapper>
				<InfoItem>
					<InfoTitle>{t('course:course_time:title')}</InfoTitle>
					<InfoContent>
						{t('course:course_time:time', { time: formatCourseTimeMinutes(video_duration_seconds) })}
					</InfoContent>
				</InfoItem>
				<InfoItem>
					<InfoTitle>{t('course:level:title')}</InfoTitle>
					<InfoContent>{t(`course:level:${class_level}`)}</InfoContent>
				</InfoItem>
			</ListItemLeftWrapper>
			<ListItemRightWrapper>
				{showStudents && (
					<InfoItem>
						<InfoTitle>{t('course:student_number:title')}</InfoTitle>
						<InfoContent>{t('course:student_number:number', { number: students })}</InfoContent>
					</InfoItem>
				)}
				{isUnlimitedDaysVisible && (
					<InfoItem>
						<InfoContent>{accessRight}</InfoContent>
					</InfoItem>
				)}
			</ListItemRightWrapper>
		</CourseInfoWrapper>
	);
};

export default CourseInfo;
